/**
 * dashboard/classes/$classId/quiz/$activationId/live.tsx — Tutor live
 * quiz control page (Phase G.4).
 *
 * Loads GET /api/quiz/activation/:id to get the title + totalQuestions
 * (the WS sends question state but not the title) → renders
 * <QuizLiveControl> which owns the WS connection + tutor actions.
 *
 * Redirects to /login if no session, and to /dashboard if not an
 * approved tutor. Admins can also view (handy for QA).
 */

import { type ReactNode, useEffect, useState } from 'react';
import { createFileRoute, redirect, useParams } from '@tanstack/react-router';
import { PageHeader } from '../../../../../../components/ui/PageHeader.js';
import { StatePanel } from '../../../../../../components/ui/StatePanel.js';
import { QuizLiveControl } from '../../../../../../components/tutor/QuizLiveControl.js';
import { BackToSessionLink } from '../../../../../../components/quiz/BackToSessionLink.js';
import { freshSessionQuery } from '../../../../../../lib/api/queries.js';

export const Route = createFileRoute('/dashboard/classes/$classId/quiz/$activationId/live')({
  // `from` is set by the in-room LiveQuizBanner so the tutor can jump
  // straight back into the ongoing RC session (BackToSessionLink).
  validateSearch: (search: Record<string, unknown>) => ({
    from: typeof search.from === 'string' ? search.from : undefined,
  }),
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({ to: '/login', search: { error: undefined, redirect: location.href } });
    }
    if (user.role === 'parent') {
      throw redirect({ to: '/parent' });
    }
    if (user.role !== 'tutor' && user.role !== 'admin') {
      throw redirect({ to: '/' });
    }
    if (user.role === 'tutor' && !user.isApprovedTutor) {
      throw redirect({ to: '/me' });
    }
    return null;
  },
  component: TutorLivePage,
});

interface ActivationSummary {
  activationId: string;
  classId: string;
  quizId: string;
  status: 'live' | 'ended';
  currentQuestionIndex: number | null;
  quizTitle: string;
  totalQuestions: number;
}

function TutorLivePage(): ReactNode {
  const { classId, activationId } = useParams({ strict: false }) as { classId: string; activationId: string };
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ActivationSummary | null>(null);
  const [userName, setUserName] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const { from: fromParam } = Route.useSearch();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r, sessRes] = await Promise.all([
          fetch(`/api/quiz/activation/${encodeURIComponent(activationId)}`, { credentials: 'include' }),
          fetch('/api/auth/me', { credentials: 'include' }),
        ]);
        const j = (await r.json()) as { ok: boolean; data?: ActivationSummary; error?: { message: string } };
        const sess = (await sessRes.json()) as { ok?: boolean; data?: { user?: { userId: string; name: string } } };
        if (cancelled) return;
        if (!j.ok || !j.data) {
          setLoadError(j.error?.message ?? 'Gagal memuat aktivasi');
          setLoading(false);
          return;
        }
        setSummary(j.data);
        setUserId(sess.data?.user?.userId ?? '');
        setUserName(sess.data?.user?.name ?? '');
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setLoadError((err as Error).message);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activationId]);

  if (loading) {
    return (
      <main className="page-shell">
        <PageHeader eyebrow="Live Quiz" title="Memuat…" />
      </main>
    );
  }
  if (loadError || !summary) {
    return (
      <main className="page-shell">
        <PageHeader eyebrow="Live Quiz" title="Aktivasi tidak ditemukan" />
        <StatePanel variant="error" title="Gagal memuat" description={loadError ?? 'Tidak ditemukan'} />
      </main>
    );
  }

  return (
    <main className="page-shell" data-testid="tutor-live">
      <div className="quiz-back-nav">
        <BackToSessionLink
          from={fromParam}
          classId={classId}
          fallbackHref={`/dashboard/classes/${classId}/quiz`}
          fallbackLabel="← Kembali ke halaman quiz kelas"
        />
      </div>
      <QuizLiveControl
        activationId={summary.activationId}
        classId={classId}
        userId={userId}
        displayName={userName}
        initialQuizTitle={summary.quizTitle}
        initialTotalQuestions={summary.totalQuestions}
      />
    </main>
  );
}
